import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Flex,
  useToast,
  Alert,
  AlertIcon,
  Spinner,
  IconButton,
  useBreakpointValue,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  InputGroup,
  InputRightElement,
  useDisclosure,
  Select
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './css/DeliveryMonitoring.css';
import { FiSearch, FiRefreshCw, FiShare2, FiCalendar, FiChevronLeft, FiChevronRight, FiClipboard, FiNavigation } from 'react-icons/fi';
import PageHeader from './shared/PageHeader';
import { AiFillFilePdf } from 'react-icons/ai';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { getDeliveryRecords, getCustomers } from '../utils/firebaseUtils';
import type { DeliveryRecord, DeliverySearchFilters } from '../types/delivery';
import type { Customer } from '../types/customer';
import ChecklistViewer from './subComponents/ChecklistViewer';
import { exportDetailedPDF } from '../utils/deliveryExportPdf';
import { calculateInternalAging, calculateCustomerAging } from '../utils/delivery';
import VehicleMapModal from './VehicleMapModal';


const DeliveryMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCompany, loadCompanyTemplate, getTemplateLabel } = useAuth();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // State
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Customer state
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // Summary data (queried on page load)
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [todayDispatched, setTodayDispatched] = useState<DeliveryRecord[]>([]);
  const [pendingDeliveries, setPendingDeliveries] = useState<DeliveryRecord[]>([]);
  const [todayCompleted, setTodayCompleted] = useState<DeliveryRecord[]>([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'dispatched' | 'pending' | 'completed' | null>(null);

  // Checklist modal state
  const { isOpen: isChecklistModalOpen, onOpen: onChecklistModalOpen, onClose: onChecklistModalClose } = useDisclosure();

  useEffect(() => {
    if (selectedCompany && selectedCompany.companyId && loadCompanyTemplate) {
      loadCompanyTemplate(selectedCompany.companyId).catch(() => {});
    }
  }, [selectedCompany, loadCompanyTemplate]);
  
  // Debug: Log company config to verify GPR hiding setting
  useEffect(() => {
    console.log('🔍 Delivery Monitoring - Company Config:', {
      companyId: selectedCompany?.companyId,
      companyName: selectedCompany?.companyName,
      isHideGPR_UserApp: selectedCompany?.companyConfig?.isHideGPR_UserApp,
      fullConfig: selectedCompany?.companyConfig
    });
  }, [selectedCompany]);
  
  const [selectedChecklistId, setSelectedChecklistId] = useState<string | null>(null);

  // Vehicle map modal state
  const [isVehicleMapOpen, setIsVehicleMapOpen] = useState(false);
  const [selectedDeliveryForMap, setSelectedDeliveryForMap] = useState<DeliveryRecord | null>(null);

  // Calculate today boundaries
  const today = new Date();
  today.setHours(23, 59, 59, 999); // Set to end of today to include full day
  const now = new Date(); // Current moment for filtering future dates
  now.setHours(23, 59, 59, 999); // Set to end of current day to allow today selection
  
  // Create date boundaries for the last 6 months
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(today.getMonth() - 6);
  
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
  const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

  // Fetch summary deliveries on mount
  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        // Dispatched today
        console.log('Query today dispatched');
        const filtersDispatchedToday: DeliverySearchFilters = {
          fromDate: startOfToday,
          dateField: 'dispatchDateTime',
          toDate: endOfToday,
          company: selectedCompany?.companyId
        };
        const dispatchDeliveriesToday = await getDeliveryRecords(filtersDispatchedToday);
        setTodayDispatched(dispatchDeliveriesToday.filter(d => {
          if (!d.dispatchDateTime) return false;
          const dt = new Date(d.dispatchDateTime);
          return dt >= startOfToday && dt <= endOfToday;
        }));

        // Completed today
        console.log('Query today completed');
        const filtersCompletedToday: DeliverySearchFilters = {
          fromDate: startOfToday,
          toDate: endOfToday,
          dateField: 'completed_at',
          isCompleted: true,
          company: selectedCompany?.companyId
        };
        const completedDeliveriesToday = await getDeliveryRecords(filtersCompletedToday);
        setTodayCompleted(completedDeliveriesToday.filter(d => {
          if (!d.isCompleted || !d.completed_at) return false;
          if (d.isCompleted !== true) return false;
          const ut = new Date(d.completed_at);
          return ut >= startOfToday && ut <= endOfToday;
        }));

        // All pending deliveries
        console.log('Query all pending deliveries');
        const pendingFilters: Partial<DeliverySearchFilters> = {
          company: selectedCompany?.companyId,
          isCompleted: false
        };
        const pendingResults = await getDeliveryRecords(pendingFilters as DeliverySearchFilters);
        setPendingDeliveries(pendingResults);
      } catch (e) {
        toast({ title: 'Failed to load summary', status: 'error', duration: 4000 });
      } finally {
        setSummaryLoading(false);
      }
    };
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany?.companyId]);

  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!selectedCompany?.id) return;
      
      setCustomersLoading(true);
      try {
        const customersList = await getCustomers(selectedCompany.companyId);
        
        setCustomers(customersList);
      } catch (error) {
        console.error('Error fetching customers:', error);
        toast({ 
          title: 'Failed to load customers', 
          status: 'error', 
          duration: 4000 
        });
      } finally {
        setCustomersLoading(false);
      }
    };
    
    fetchCustomers();
  }, [selectedCompany?.companyId, toast]);

  // Search filters
  const [filters, setFilters] = useState<DeliverySearchFilters>(() => {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 5);
    fromDate.setHours(0, 0, 0, 0); // Set to 00:00:00

    const toDate = new Date();
    toDate.setHours(23, 59, 59, 999); // Set to 23:59:59

    return {
      wayBillNumber: '',
      tlo: '',
      plateNumber: '',
      customerId: '',
      fromDate,
      toDate
    };
  });

  // Handle search
  const handleSearch = async () => {

  // ...existing code...
    if (!filters.fromDate || !filters.toDate) {
      toast({
        title: 'Error',
        description: 'Both From Date and To Date are required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (filters.fromDate > filters.toDate) {
      toast({
        title: 'Error',
        description: 'From Date cannot be later than To Date',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setSearching(true);
    setLoading(true);

    try {
      // Ensure dates are valid before calling the function
      const searchFilters: DeliverySearchFilters = {
        wayBillNumber: filters.wayBillNumber,
        tlo: filters.tlo,
        dateField: 'dispatchDateTime',
        plateNumber: filters.plateNumber,
        fromDate: filters.fromDate!, // We've already validated these are not null
        toDate: filters.toDate!,     // We've already validated these are not null
        company: selectedCompany?.companyId, // Restrict search to user's company
        customerId: filters.customerId
      };

      const results = await getDeliveryRecords(searchFilters);
      console.log('Search results:', results);
      setDeliveries(results);
      setHasSearched(true);

      if (results.length === 0) {
        toast({
          title: 'No Results',
          description: 'No delivery records found for the specified criteria',
          status: 'info',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error searching deliveries:', error);
      toast({
        title: 'Search Error',
        description: 'Failed to search delivery records. Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  // Format date for display
  const formatDateTime = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate GPR (Gross Profit Rate)
  const calculateGPR = (delivery: DeliveryRecord) => {
    const contractPrice = typeof delivery.estContractPrice === 'string' 
      ? parseFloat(delivery.estContractPrice) 
      : delivery.estContractPrice || 0;
    
    const deliveryCost = typeof delivery.estDeliveryCost === 'string' 
      ? parseFloat(delivery.estDeliveryCost) 
      : delivery.estDeliveryCost || 0;
    
    // Avoid division by zero
    if (deliveryCost === 0) return '-';

    // Calculate GPR as percentage: (estContractPrice / estDeliveryCost) * 100
    const gpr = ((contractPrice - deliveryCost)/contractPrice) * 100;
    return `${gpr.toFixed(2)}%`;
  };


  // Reset search
  const handleReset = () => {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 5);
    fromDate.setHours(0, 0, 0, 0); // Set to 00:00:00

    const toDate = new Date();
    toDate.setHours(23, 59, 59, 999); // Set to 23:59:59

    setFilters({
      wayBillNumber: '',
      tlo: '',
      plateNumber: '',
      customerId: '',
      fromDate,
      toDate
    });
    setDeliveries([]);
    setHasSearched(false);
  };

  // Handle checklist view
  const handleViewChecklist = (checklistId: string) => {
    setSelectedChecklistId(checklistId);
    onChecklistModalOpen();
  };

  const handleOpenVehicleMap = (delivery: DeliveryRecord) => {
    setSelectedDeliveryForMap(delivery);
    setIsVehicleMapOpen(true);
  };

  const handleCloseVehicleMap = () => {
    setIsVehicleMapOpen(false);
    setSelectedDeliveryForMap(null);
  };

  // Export summary data to PDF
  const handleExportSummaryPDF = async () => {
    try {
      let currentData: DeliveryRecord[] = [];
      let reportTitle = '';
      
      switch (modalType) {
        case 'dispatched':
          currentData = todayDispatched;
          reportTitle = 'Today Dispatched Deliveries Report';
          break;
        case 'pending':
          currentData = pendingDeliveries;
          reportTitle = 'Pending Deliveries Report';
          break;
        case 'completed':
          currentData = todayCompleted;
          reportTitle = 'Today Completed Deliveries Report';
          break;
        default:
          return;
      }

      // Use the consolidated export function
      await exportDetailedPDF(
        currentData,
        reportTitle,
        selectedCompany?.companyName,
        toast
      );
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: 'Export Failed',
        description: 'Failed to export PDF. Please try again.',
        status: 'error',
        duration: 5000,
      });
    }
  };


  // Mobile Card View for deliveries
  const DeliveryCard: React.FC<{ delivery: DeliveryRecord }> = ({ delivery }) => {
    const cardRef = React.useRef<HTMLDivElement>(null);
    const toast = useToast();

    const handleShare = async () => {
      if (!cardRef.current) return;
      try {
        const originalWidth = cardRef.current.style.width;
        const originalMargin = cardRef.current.style.margin;
        cardRef.current.style.width = '450px';
        cardRef.current.style.margin = '20px';
        
        // Wait for layout to settle
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const canvas = await html2canvas(cardRef.current, { 
          useCORS: true, 
          logging: false, 
          scale: 2,
          height: cardRef.current.scrollHeight,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });
        cardRef.current.style.width = originalWidth;
        cardRef.current.style.margin = originalMargin;
        const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('Failed to capture screenshot');
        if (navigator.canShare && navigator.canShare({ files: [new File([blob], 'delivery.png', { type: 'image/png' })] })) {
          await navigator.share({
            files: [new File([blob], 'delivery.png', { type: 'image/png' })],
            title: `Delivery WayBill# ${delivery.wayBillNumber}`,
            text: 'Shared from Mobile Reporting App',
          });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'delivery.png';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast({ title: 'Screenshot downloaded', status: 'info', duration: 3000 });
        }
      } catch (err) {
        toast({ title: 'Failed to capture screenshot', status: 'error', duration: 3000 });
      }
    };

    return (
      <Card 
        key={delivery.id} 
        mb={6} 
        ref={cardRef} 
        position="relative" 
        pt={0}
        bg="rgba(255, 255, 255, 0.9)"
        backdropFilter="blur(10px)"
        borderRadius="2xl"
        boxShadow="0 10px 30px rgba(0,0,0,0.1)"
        border="1px solid"
        borderColor="gray.200"
        _hover={{
          transform: "translateY(-2px)",
          boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
        }}
        transition="all 0.3s"
      >
        <CardBody p={6} pb={4} position="relative">
          <Box position="absolute" top={2} right={2} zIndex={10}>
            <IconButton
              aria-label="Share this delivery"
              icon={<FiShare2 />}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              onClick={handleShare}
              borderRadius="xl"
              _hover={{
                bg: "blue.50",
                transform: "scale(1.1)"
              }}
              transition="all 0.2s"
            />
          </Box>
          <VStack align="stretch" spacing={4}>
            <Box textAlign="left">
              {/* COMPLETED indicator */}
              {delivery.isCompleted && (
                <Box 
                  bg="linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(5, 150, 105, 0.1))"
                  borderRadius="xl"
                  px={4}
                  py={2}
                  mb={4}
                  border="1px solid"
                  borderColor="green.200"
                >
                  <HStack spacing={2}>
                    <Text fontSize="lg">✅</Text>
                    <Text fontSize="md" fontWeight="700" color="green.700">
                      COMPLETED
                    </Text>
                  </HStack>
                </Box>
              )}
              
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between" wrap="wrap" px={2}>
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>TLO/DR</Text>
                    <Text fontSize="lg" fontWeight="700" color="blue.600">{delivery.tlo}</Text>
                  </Box>
                  <Box textAlign="right" mr={10}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>WayBill</Text>
                    <Text fontSize="lg" fontWeight="700" color="purple.600">{delivery.wayBillNumber}</Text>
                  </Box>
                </HStack>
                
                <Box 
                  bg="gray.50" 
                  borderRadius="xl" 
                  p={4}
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <VStack spacing={3} align="stretch">
                    <HStack>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'customer', 'Customer') + ':'}</Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.customerName}</Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'route', 'Route') + ':'}</Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.route}</Text>
                    </HStack>
                    {delivery.cargoWeight && (
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" color="red.600" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'cargoWeight', 'Cargo Weight') + ':'}</Text>
                        <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.cargoWeight}</Text>
                      </HStack>
                    )}
                    {delivery.approvedDeadWeight && (
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'approvedDeadWeight', 'Approved DW') + ':'}</Text>
                        <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.approvedDeadWeight}</Text>
                      </HStack>
                    )}
                    <HStack>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'plateNumber', 'Plate') + ':'}</Text>
                      <HStack spacing={2} flex={1}>
                        <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.plateNumber}</Text>
                        <IconButton
                          aria-label="Live Track Vehicle"
                          icon={<FiNavigation />}
                          size="xs"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleOpenVehicleMap(delivery)}
                          title="Live Track Vehicle"
                          _hover={{ 
                            bg: "blue.100",
                            transform: "scale(1.1)"
                          }}
                          transition="all 0.2s"
                        />
                        {delivery.checklistId && (
                          <IconButton
                            aria-label="View Pre-Departure Checklist"
                            icon={<FiClipboard />}
                            size="xs"
                            variant="ghost"
                            colorScheme="green"
                            onClick={() => handleViewChecklist(delivery.checklistId)}
                            title="View Pre-Departure Checklist"
                            _hover={{ 
                              bg: "green.100",
                              transform: "scale(1.1)"
                            }}
                            transition="all 0.2s"
                          />
                        )}
                      </HStack>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'driver', 'Driver') + ':'}</Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.driverName}</Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'driverMobile', 'Mobile') + ':'}</Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.driverMobile}</Text>
                    </HStack>
                  </VStack>
                </Box>

                <Box 
                  bg="blue.50" 
                  borderRadius="xl" 
                  p={4}
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <VStack spacing={3} align="stretch">
                    <HStack>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'dispatchDateTime', 'Dispatched') + ':'}</Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="500">{formatDateTime(delivery.dispatchDateTime)}</Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'arrivalLoadingArea', 'Loading Area') + ':'}</Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.arrivalLoadingArea ? formatDateTime(delivery.arrivalLoadingArea) : 'N/A'}</Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'arrivalUnloadingArea', 'Unloading Area') + ':'}</Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.arrivalUnloadingArea ? formatDateTime(delivery.arrivalUnloadingArea) : 'N/A'}</Text>
                    </HStack>
                    {delivery.statusDetails && (
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'statusDetails', 'Status') + ':'}</Text>
                        <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.statusDetails || 'N/A'}</Text>
                      </HStack>
                    )}
                    {delivery.completed_at ? (
                    <HStack>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'completed_at', 'Completed') + ':'}</Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.completed_at ? formatDateTime(delivery.completed_at) : 'N/A'}</Text>
                    </HStack>
                    ):null}
                  </VStack>
                </Box>

                {delivery.demurrageCounting || delivery.billingStatus || delivery.podReturnedAt || delivery.completed_at || delivery.customerAgingDays || (delivery.estContractPrice && !selectedCompany?.companyConfig?.isHideGPR_UserApp) ? (
                  <Box
                    bg="orange.50"
                    borderRadius="xl"
                    p={4}
                    border="1px solid"
                  borderColor="orange.200"
                >
                  <VStack spacing={3} align="stretch">
                    {delivery.demurrageCounting ? (
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" color="red.600" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'demurrageCounting', 'Demurrage') + ':'}</Text>
                        <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.demurrageCounting}</Text>
                      </HStack>
                    ) : null}
                    {delivery.billingStatus ? (
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" color="red.600" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'billingStatus', 'Billing Status') + ':'}</Text>
                        <Text fontSize="sm" color="blue.600" fontWeight="500">{delivery.billingStatus}</Text>
                      </HStack>
                    ) : null}
                    {delivery.podReturnedAt ? (
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">POD Returned:</Text>
                        <Text fontSize="sm" color="blue.600" fontWeight="500">{formatDateTime(delivery.podReturnedAt)}</Text>
                      </HStack>
                    ) : null}
                    {delivery.completed_at ? (
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'internalAging', 'Internal Aging') + ':'}</Text>
                        <Text fontSize="sm" color="blue.600" fontWeight="500">{calculateInternalAging(delivery)}</Text>
                      </HStack>
                    ) : null}
                    {delivery.customerAgingDays ? (
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'customerAging', 'Customer Aging') + ':'}</Text>
                        <Text fontSize="sm" color="blue.600" fontWeight="500">{calculateCustomerAging(delivery)}</Text>
                      </HStack>
                    ) : null}
                    {delivery.estContractPrice && !selectedCompany?.companyConfig?.isHideGPR_UserApp ? (
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" color="gray.700" minW="120px">{getTemplateLabel(selectedCompany?.companyId, 'gpr', 'GPR') + ':'}</Text>
                          <Text fontSize="sm" color="green.600" fontWeight="500">{calculateGPR(delivery)}</Text>
                      </HStack>
                    ) : null}
                  </VStack>
                </Box>) : null}

    
              </VStack>

              <Box 
                bg="linear-gradient(135deg, rgba(156, 163, 175, 0.1), rgba(107, 114, 128, 0.1))" 
                borderRadius="xl" 
                p={4} 
                mt={4}
                border="1px solid" 
                borderColor="gray.200"
              >
                <VStack spacing={2} align="stretch">
                  <HStack>
                    <Text fontSize="xs" fontWeight="600" color="gray.600" minW="80px">Updated:</Text>
                    <Text fontSize="xs" color="gray.500" fontWeight="500">{delivery.updated_at ? formatDateTime(delivery.updated_at) : 'N/A'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontSize="xs" fontWeight="600" color="gray.600" minW="80px">By:</Text>
                    <Text 
                      fontSize="xs" 
                      color="gray.500" 
                      fontWeight="500"
                      flex="1"
                      wordBreak="break-word"
                    >
                      {delivery.updated_by ? delivery.updated_by : 'N/A'}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)">
      <PageHeader
        title="Delivery Monitoring"
        subtitle="Track and manage deliveries"
        icon="🚚"
        onBack={() => navigate('/dashboard')}
      />

      {/* Main Content */}
      <Container maxW="6xl" py={{ base: 8, md: 12 }} px={{ base: 4, md: 8 }}>
        <VStack spacing={8} align="stretch">
          {/* Delivery Summary Cards (queried on page load) */}
          <VStack spacing={4}>
            <Text 
              fontSize="xl" 
              fontWeight="700" 
              bgGradient="linear(135deg, gray.700, gray.900)"
              bgClip="text"
              textAlign="center"
            >
              📊 Today's Overview
            </Text>
            <HStack spacing={4} align="stretch" justify="center" w="100%">
              <Card
                as="button"
                bg="linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.1))"
                borderWidth="1px"
                borderColor="blue.200"
                shadow="lg"
                borderRadius="2xl"
                width="33.33%"
                px={3} 
                py={4}
                onClick={() => { if (!summaryLoading) { setModalType('dispatched'); setModalOpen(true); } }}
                _hover={{ 
                  boxShadow: "xl", 
                  transform: "translateY(-2px)",
                  bg: "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(99, 102, 241, 0.15))"
                }}
                cursor={summaryLoading ? 'not-allowed' : 'pointer'}
                pointerEvents={summaryLoading ? 'none' : 'auto'}
                opacity={summaryLoading ? 0.6 : 1}
                transition="all 0.3s"
                backdropFilter="blur(10px)"
              >
                <CardBody p={3} textAlign="center">
                  <VStack spacing={2} align="center">
                    <Text fontSize="2xl">🚚</Text>
                    <VStack spacing={1} align="center">
                      <Text fontSize="xs" fontWeight="600" color="blue.700" textAlign="center">Dispatched Today</Text>
                      <Text fontSize="2xl" fontWeight="900" bgGradient="linear(135deg, blue.600, blue.800)" bgClip="text" textAlign="center">
                        {summaryLoading ? <Spinner size="sm" color="blue.500" /> : todayDispatched.length}
                      </Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
              
              <Card
                as="button"
                bg="linear-gradient(135deg, rgba(251, 146, 60, 0.1), rgba(245, 101, 101, 0.1))"
                borderWidth="1px"
                borderColor="orange.200"
                shadow="lg"
                borderRadius="2xl"
                width="33.33%"
                px={3} 
                py={4}
                onClick={() => { if (!summaryLoading) { setModalType('pending'); setModalOpen(true); } }}
                _hover={{ 
                  boxShadow: "xl", 
                  transform: "translateY(-2px)",
                  bg: "linear-gradient(135deg, rgba(251, 146, 60, 0.15), rgba(245, 101, 101, 0.15))"
                }}
                cursor={summaryLoading ? 'not-allowed' : 'pointer'}
                pointerEvents={summaryLoading ? 'none' : 'auto'}
                opacity={summaryLoading ? 0.6 : 1}
                transition="all 0.3s"
                backdropFilter="blur(10px)"
              >
                <CardBody p={3} textAlign="center">
                  <VStack spacing={2} align="center">
                    <Text fontSize="2xl">⏳</Text>
                    <VStack spacing={1} align="center">
                      <Text fontSize="xs" fontWeight="600" color="orange.700" textAlign="center">Pending Deliveries</Text>
                      <Text fontSize="2xl" fontWeight="900" bgGradient="linear(135deg, orange.600, orange.800)" bgClip="text" textAlign="center">
                        {summaryLoading ? <Spinner size="sm" color="orange.500" /> : pendingDeliveries.length}
                      </Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
              
              <Card
                as="button"
                bg="linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(5, 150, 105, 0.1))"
                borderWidth="1px"
                borderColor="green.200"
                shadow="lg"
                borderRadius="2xl"
                width="33.33%"
                px={3} 
                py={4}
                onClick={() => { if (!summaryLoading) { setModalType('completed'); setModalOpen(true); } }}
                _hover={{ 
                  boxShadow: "xl", 
                  transform: "translateY(-2px)",
                  bg: "linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(5, 150, 105, 0.15))"
                }}
                cursor={summaryLoading ? 'not-allowed' : 'pointer'}
                pointerEvents={summaryLoading ? 'none' : 'auto'}
                opacity={summaryLoading ? 0.6 : 1}
                transition="all 0.3s"
                backdropFilter="blur(10px)"
              >
                <CardBody p={3} textAlign="center">
                  <VStack spacing={2} align="center">
                    <Text fontSize="2xl">✅</Text>
                    <VStack spacing={1} align="center">
                      <Text fontSize="xs" fontWeight="600" color="green.700" textAlign="center">Completed Today</Text>
                      <Text fontSize="2xl" fontWeight="900" bgGradient="linear(135deg, green.600, green.800)" bgClip="text" textAlign="center">
                        {summaryLoading ? <Spinner size="sm" color="green.500" /> : todayCompleted.length}
                      </Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </HStack>
          </VStack>

          {/* Modal for summary card results */}
          <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size={isMobile ? 'full' : 'xl'}>
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
            <ModalContent 
              bg="white"
              borderRadius="2xl"
              boxShadow="0 25px 50px rgba(0,0,0,0.25)"
              border="1px solid"
              borderColor="gray.200"
            >
              <ModalHeader
                bg="linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.1))"
                borderTopRadius="2xl"
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <VStack spacing={2} align="start">
                  <Text 
                    fontSize="lg"
                    fontWeight="800"
                    bgGradient="linear(135deg, blue.600, purple.600)"
                    bgClip="text"
                  >
                    {modalType === 'dispatched' && '🚚 Today Dispatched Deliveries'}
                    {modalType === 'pending' && '⏳ Pending Deliveries'}
                    {modalType === 'completed' && '✅ Today Completed Deliveries'}
                  </Text>
                  <HStack justify="space-between" w="100%">
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Total Records: {
                        modalType === 'dispatched' ? todayDispatched.length :
                        modalType === 'pending' ? pendingDeliveries.length :
                        modalType === 'completed' ? todayCompleted.length : 0
                      }
                    </Text>
                    <IconButton
                      aria-label="Export to PDF"
                      icon={<AiFillFilePdf />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={handleExportSummaryPDF}
                      isDisabled={summaryLoading || (
                        modalType === 'dispatched' ? todayDispatched.length === 0 :
                        modalType === 'pending' ? pendingDeliveries.length === 0 :
                        modalType === 'completed' ? todayCompleted.length === 0 : true
                      )}
                      _hover={{
                        bg: "red.50",
                        transform: "translateY(-1px)"
                      }}
                      transition="all 0.2s"
                      title="Export to PDF"
                    />
                  </HStack>
                </VStack>
              </ModalHeader>
              <ModalCloseButton 
                borderRadius="full"
                _hover={{
                  bg: "red.50",
                  transform: "scale(1.1)"
                }}
                transition="all 0.2s"
              />
              <ModalBody>
                {summaryLoading ? (
                  <Flex justify="center" py={8}><Spinner size="xl" color="blue.500" /></Flex>
                ) : (
                  <VStack spacing={4} align="stretch">
                    {modalType === 'dispatched' && todayDispatched.length === 0 && <Text>No dispatched deliveries today.</Text>}
                    {modalType === 'pending' && pendingDeliveries.length === 0 && <Text>No pending deliveries.</Text>}
                    {modalType === 'completed' && todayCompleted.length === 0 && <Text>No completed deliveries today.</Text>}
                    {modalType === 'dispatched' && todayDispatched.map(d => <DeliveryCard key={d.id} delivery={d} />)}
                    {modalType === 'pending' && pendingDeliveries.map(d => <DeliveryCard key={d.id} delivery={d} />)}
                    {modalType === 'completed' && todayCompleted.map(d => <DeliveryCard key={d.id} delivery={d} />)}
                  </VStack>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
          
          {/* Search Filters */}
          <Card 
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            boxShadow="0 20px 40px rgba(0,0,0,0.1)"
            border="1px solid"
            borderColor="whiteAlpha.300"
          >
            <CardBody p={8}>
              <VStack spacing={6} align="stretch">
                <VStack spacing={2}>
                  <Text 
                    fontSize="xl" 
                    fontWeight="700" 
                    bgGradient="linear(135deg, gray.700, gray.900)"
                    bgClip="text"
                    textAlign="center"
                  >
                    🔍 Search Deliveries
                  </Text>
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    Find delivery records using various filters
                  </Text>
                </VStack>
                
                {/* Customer Selection Row */}
                <FormControl>
                  <FormLabel fontWeight="600" color="gray.700">Select Customer</FormLabel>
                  <Select
                    placeholder="Select Customer"
                    value={filters.customerId}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      customerId: e.target.value
                    }))}
                    borderRadius="xl"
                    borderColor="gray.300"
                    _hover={{ borderColor: "blue.400" }}
                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                    bg="white"
                    size="lg"
                    isDisabled={customersLoading}
                  >
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.customerName}
                      </option>
                    ))}
                  </Select>
                  {customersLoading && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Loading customers...
                    </Text>
                  )}
                </FormControl>
                
                {/* Search Fields Row 1 */}
                <Flex 
                  direction={{ base: "column", md: "row" }} 
                  gap={6}
                >
                  <FormControl flex={1}>
                    <FormLabel fontWeight="600" color="gray.700">WayBill Number</FormLabel>
                    <Input
                      placeholder="Enter waybill number"
                      value={filters.wayBillNumber}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        wayBillNumber: e.target.value
                      }))}
                      borderRadius="xl"
                      borderColor="gray.300"
                      _hover={{ borderColor: "blue.400" }}
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                      bg="white"
                      size="lg"
                    />
                  </FormControl>

                  <FormControl flex={1}>
                    <FormLabel fontWeight="600" color="gray.700">TLO/DR Number</FormLabel>
                    <Input
                      placeholder="Enter TLO/DR number"
                      value={filters.tlo}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        tlo: e.target.value
                      }))}
                      borderRadius="xl"
                      borderColor="gray.300"
                      _hover={{ borderColor: "blue.400" }}
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                      bg="white"
                      size="lg"
                    />
                  </FormControl>
                </Flex>

                {/* Date Range Row 2 */}
                <Flex 
                  direction={{ base: "column", md: "row" }} 
                  gap={6}
                >
                  <FormControl flex={1} isRequired>
                    <FormLabel fontWeight="600" color="gray.700">
                      <HStack spacing={2}>
                        <FiCalendar />
                        <Text>Dispatched FROM Date</Text>
                      </HStack>
                    </FormLabel>
                    <InputGroup className="reports-date-input-group" w="100%" position="relative">
                      <div className="reports-datepicker-wrapper" style={{ width: '100%' }}>
                        <DatePicker
                          selected={filters.fromDate}
                          onChange={(date: Date | null) => setFilters(prev => ({
                            ...prev,
                            fromDate: date
                          }))}
                          dateFormat="yyyy-MM-dd"
                          minDate={sixMonthsAgo}
                          maxDate={filters.toDate || today}
                          filterDate={(date) => date <= now}
                          placeholderText="Select from date"
                          className="chakra-input custom-datepicker"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          withPortal
                          portalId="delivery-date-picker-1"
                          showPopperArrow={false}
                          renderCustomHeader={({
                            date,
                            decreaseMonth,
                            increaseMonth,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled,
                          }) => (
                            <div className="react-datepicker__header">
                              <button
                                type="button"
                                className="react-datepicker__navigation react-datepicker__navigation--previous"
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                style={{
                                  position: 'absolute',
                                  left: '12px',
                                  top: '18px',
                                  width: '32px',
                                  height: '32px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  border: '2px solid #3182ce',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  color: '#3182ce',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                  zIndex: 9999999999999
                                }}
                              >
                                <FiChevronLeft size={20} />
                              </button>
                              
                              <div className="react-datepicker__current-month">
                                {date.toLocaleDateString('en-US', {
                                  month: 'long',
                                  year: 'numeric',
                                })}
                              </div>
                              
                              <button
                                type="button"
                                className="react-datepicker__navigation react-datepicker__navigation--next"
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                style={{
                                  position: 'absolute',
                                  right: '12px',
                                  top: '18px',
                                  width: '32px',
                                  height: '32px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  border: '2px solid #3182ce',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  color: '#3182ce',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                  zIndex: 9999999999999
                                }}
                              >
                                <FiChevronRight size={20} />
                              </button>
                            </div>
                          )}
                        />
                      </div>
                      <InputRightElement pointerEvents="none">
                        <FiCalendar color="#3182ce" />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                  <FormControl flex={1} isRequired>
                    <FormLabel fontWeight="600" color="gray.700">
                      <HStack spacing={2}>
                        <FiCalendar />
                        <Text>Dispatched TO Date</Text>
                      </HStack>
                    </FormLabel>
                    <InputGroup className="reports-date-input-group" w="100%" position="relative">
                      <div className="reports-datepicker-wrapper" style={{ width: '100%' }}>
                        <DatePicker
                          selected={filters.toDate}
                          onChange={(date: Date | null) => setFilters(prev => ({
                            ...prev,
                            toDate: date
                          }))}
                          dateFormat="yyyy-MM-dd"
                          minDate={sixMonthsAgo}
                          maxDate={today}
                          filterDate={(date) => date <= now}
                          placeholderText="Select to date"
                          className="chakra-input custom-datepicker"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          withPortal
                          portalId="delivery-date-picker-2"
                          showPopperArrow={false}
                          renderCustomHeader={({
                            date,
                            decreaseMonth,
                            increaseMonth,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled,
                          }) => (
                            <div className="react-datepicker__header">
                              <button
                                type="button"
                                className="react-datepicker__navigation react-datepicker__navigation--previous"
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                style={{
                                  position: 'absolute',
                                  left: '12px',
                                  top: '18px',
                                  width: '32px',
                                  height: '32px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  border: '2px solid #3182ce',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  color: '#3182ce',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                  zIndex: 9999999999999
                                }}
                              >
                                <FiChevronLeft size={20} />
                              </button>
                              
                              <div className="react-datepicker__current-month">
                                {date.toLocaleDateString('en-US', {
                                  month: 'long',
                                  year: 'numeric',
                                })}
                              </div>
                              
                              <button
                                type="button"
                                className="react-datepicker__navigation react-datepicker__navigation--next"
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                style={{
                                  position: 'absolute',
                                  right: '12px',
                                  top: '18px',
                                  width: '32px',
                                  height: '32px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  border: '2px solid #3182ce',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  color: '#3182ce',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                  zIndex: 9999999999999
                                }}
                              >
                                <FiChevronRight size={20} />
                              </button>
                            </div>
                          )}
                        />
                      </div>
                      <InputRightElement pointerEvents="none">
                        <FiCalendar color="#3182ce" />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                </Flex>

                {/* Action Buttons */}
                <Flex gap={4} justify="center" pt={4}>
                  <Button
                    flex={{ base: "unset", md: 1 }}
                    leftIcon={<FiSearch />}
                    bgGradient="linear(135deg, blue.500, purple.600)"
                    color="white"
                    size="lg"
                    onClick={handleSearch}
                    isLoading={searching}
                    loadingText="Searching..."
                    isDisabled={!filters.fromDate || !filters.toDate || searching}
                    px={8}
                    py={3}
                    borderRadius="xl"
                    _hover={{
                      bgGradient: "linear(135deg, blue.600, purple.700)",
                      transform: "translateY(-2px)",
                      boxShadow: "xl"
                    }}
                    _active={{
                      transform: "translateY(0)"
                    }}
                    transition="all 0.2s"
                    fontWeight="700"
                    boxShadow="lg"
                  >
                    Search
                  </Button>
                  <Button
                    flex={{ base: "unset", md: 1 }}
                    leftIcon={<FiRefreshCw />}
                    variant="outline"
                    borderColor="gray.300"
                    color="gray.700"
                    size="lg"
                    onClick={handleReset}
                    px={8}
                    py={3}
                    borderRadius="xl"
                    _hover={{
                      bg: "gray.50",
                      transform: "translateY(-2px)",
                      boxShadow: "lg"
                    }}
                    _active={{
                      transform: "translateY(0)"
                    }}
                    transition="all 0.2s"
                    fontWeight="600"
                    borderWidth="2px"
                  >
                    Reset
                  </Button>
                </Flex>
              </VStack>
            </CardBody>
          </Card>

          {/* Results Section */}
          {loading && (
            <Flex justify="center" py={12}>
              <VStack spacing={4}>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text 
                  fontSize="lg" 
                  fontWeight="600"
                  bgGradient="linear(135deg, blue.600, purple.600)"
                  bgClip="text"
                >
                  Searching deliveries...
                </Text>
              </VStack>
            </Flex>
          )}

          {hasSearched && !loading && deliveries.length === 0 && (
            <Alert 
              status="info" 
              borderRadius="xl"
              bg="blue.50"
              border="1px solid"
              borderColor="blue.200"
              py={6}
            >
              <AlertIcon color="blue.500" />
              <VStack align="start" spacing={1}>
                <Text fontWeight="600" color="blue.800">No delivery records found</Text>
                <Text fontSize="sm" color="blue.600">Try adjusting your search criteria and search again</Text>
              </VStack>
            </Alert>
          )}

          {deliveries.length > 0 && (
            <Card 
              id="delivery-results-group"
              bg="rgba(255, 255, 255, 0.9)"
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              boxShadow="0 20px 40px rgba(0,0,0,0.1)"
              border="1px solid"
              borderColor="whiteAlpha.300"
            >
              <CardHeader
                bg="linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.1))"
                borderTopRadius="2xl"
                borderBottom="1px solid"
                borderColor="gray.200"
                py={6}
              >
                <HStack justify="space-between">
                  <VStack align="start" spacing={1}>
                    <Text 
                      fontSize="xl" 
                      fontWeight="800"
                      bgGradient="linear(135deg, blue.600, purple.600)"
                      bgClip="text"
                    >
                      📋 Search Results
                    </Text>
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Found {deliveries.length} delivery records
                    </Text>
                  </VStack>
                  <HStack spacing={2}>
                    <IconButton
                      aria-label="Export Results as PDF"
                      icon={<AiFillFilePdf />}
                      size="md"
                      variant="ghost"
                      colorScheme="red"
                      borderRadius="xl"
                      _hover={{
                        bg: "red.50",
                        transform: "translateY(-1px)"
                      }}
                      transition="all 0.2s"
                      onClick={async () => {
                        await exportDetailedPDF(
                          deliveries,
                          'Delivery Group Report',
                          selectedCompany?.companyName,
                          toast
                        );
                      }}
                    />
                  </HStack>
                </HStack>
              </CardHeader>
              <CardBody>
                {isMobile ? (
                  // Mobile Card View
                  <VStack spacing={4} align="stretch">
                    {deliveries.map((delivery) => (
                      <DeliveryCard key={delivery.id} delivery={delivery} />
                    ))}
                  </VStack>
                ) : (
                  // Desktop Table View
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'wayBillNumber', 'WayBill #')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'tlo', 'TLO/DR')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'customerName', 'Customer')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'route', 'Route')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'plateNumber', 'Plate #')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'driverName', 'Driver')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'driverMobile', 'Driver Mobile')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'cargoWeight', 'Cargo Weight')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'approvedDeadWeight', 'Approved Deadweight')}</Th>
                          <Th width="100px">{getTemplateLabel(selectedCompany?.companyId, 'dispatchDateTime', 'Dispatched')}</Th>
                          <Th width="120px">{getTemplateLabel(selectedCompany?.companyId, 'arrivalLoadingArea', 'Arrival Loading Area')}</Th>
                          <Th width="120px">{getTemplateLabel(selectedCompany?.companyId, 'arrivalUnloadingArea', 'Arrival Unloading Area')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'statusDetails', 'Delivery Status')}</Th>
                          <Th width="120px">{getTemplateLabel(selectedCompany?.companyId, 'completed_at', 'Delivery Completed')}</Th>
                          <Th width="80px">{getTemplateLabel(selectedCompany?.companyId, 'demurrageCounting', 'Demurrage Counting')}</Th>
                          <Th width="100px">{getTemplateLabel(selectedCompany?.companyId, 'internalAging', 'Internal Aging')}</Th>
                          <Th width="100px">{getTemplateLabel(selectedCompany?.companyId, 'customerAging', 'Customer Aging')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'billingStatus', 'Billing Status')}</Th>
                          <Th width="160px">POD Returned</Th>
                          <Th width="100px">{getTemplateLabel(selectedCompany?.companyId, 'estContractPrice', 'Est Contract Price')}</Th>
                          <Th width="100px">{getTemplateLabel(selectedCompany?.companyId, 'estDeliveryCost', 'Est Delivery Cost')}</Th>
                          <Th width="100px">{getTemplateLabel(selectedCompany?.companyId, 'updated_at', 'Updated At')}</Th>
                          <Th>{getTemplateLabel(selectedCompany?.companyId, 'updated_by', 'Updated By')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {deliveries.map((delivery) => (
                          <Tr key={delivery.id}>
                            <Td fontWeight="semibold" color="blue.600">
                              {delivery.wayBillNumber}
                            </Td>
                            <Td>{delivery.tlo}</Td>
                            <Td>{delivery.customerName}</Td>
                            <Td>{delivery.route}</Td>
                            <Td>
                              <HStack spacing={2}>
                                <Text>{delivery.plateNumber}</Text>
                                <IconButton
                                  aria-label="Live Track Vehicle"
                                  icon={<FiNavigation />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={() => handleOpenVehicleMap(delivery)}
                                  title="Live Track Vehicle"
                                  _hover={{ 
                                    bg: "blue.100",
                                    transform: "scale(1.1)"
                                  }}
                                  transition="all 0.2s"
                                />
                                {delivery.checklistId && (
                                  <IconButton
                                    aria-label="View Pre-Departure Checklist"
                                    icon={<FiClipboard />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="green"
                                    onClick={() => handleViewChecklist(delivery.checklistId)}
                                    title="View Pre-Departure Checklist"
                                    _hover={{ 
                                      bg: "green.100",
                                      transform: "scale(1.1)"
                                    }}
                                    transition="all 0.2s"
                                  />
                                )}
                              </HStack>
                            </Td>
                            <Td>{delivery.driverName}</Td>
                            <Td>{delivery.driverMobile}</Td>
                            <Td>{delivery.cargoWeight}</Td>
                            <Td>{delivery.approvedDeadWeight}</Td>
                            <Td width="100px">{formatDateTime(delivery.dispatchDateTime)}</Td>
                            <Td width="120px">{formatDateTime(delivery.arrivalLoadingArea)}</Td>
                            <Td width="120px">{formatDateTime(delivery.arrivalUnloadingArea)}</Td>
                            <Td>{delivery.statusDetails}</Td>
                            <Td width="120px">{formatDateTime(delivery.completed_at)}</Td>
                            <Td width="80px">{delivery.demurrageCounting ?? ''}</Td>
                            <Td width="100px">{calculateInternalAging(delivery)}</Td>
                            <Td width="100px">{calculateCustomerAging(delivery)}</Td>
                            <Td>{delivery.billingStatus ?? ''}</Td>
                            <Td width="160px">{delivery.podReturnedAt ? formatDateTime(delivery.podReturnedAt) : ''}</Td>
                            <Td width="100px">{delivery.estContractPrice ? `₱${delivery.estContractPrice.toLocaleString()}` : ''}</Td>
                            <Td width="100px">{delivery.estDeliveryCost ? `₱${delivery.estDeliveryCost.toLocaleString()}` : ''}</Td>
                            <Td width="100px">{formatDateTime(delivery.updated_at) ?? ''}</Td>
                            <Td>{delivery.updated_by ?? ''}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>

      {/* Checklist Modal */}
      <Modal isOpen={isChecklistModalOpen} onClose={onChecklistModalClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text fontSize="lg" fontWeight="bold">
              Pre-Departure Checklist
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ChecklistViewer 
              checklistId={selectedChecklistId}
              showHeader={false}
              compact={false}
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onChecklistModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Vehicle Map Modal */}
      {selectedDeliveryForMap && (
        <VehicleMapModal
          isOpen={isVehicleMapOpen}
          onClose={handleCloseVehicleMap}
          deliveryId={selectedDeliveryForMap.id}
          deliveryData={{
            wayBillNumber: selectedDeliveryForMap.wayBillNumber,
            route: selectedDeliveryForMap.route,
            driverName: selectedDeliveryForMap.driverName,
            plateNumber: selectedDeliveryForMap.plateNumber,
            destination: selectedDeliveryForMap.customerName,
          }}
          originLat={selectedDeliveryForMap.plantLat ? parseFloat(selectedDeliveryForMap.plantLat) : undefined}
          originLng={selectedDeliveryForMap.plantLong ? parseFloat(selectedDeliveryForMap.plantLong) : undefined}
          destinationLat={selectedDeliveryForMap.deliveryLat ? parseFloat(selectedDeliveryForMap.deliveryLat) : undefined}
          destinationLng={selectedDeliveryForMap.deliveryLong ? parseFloat(selectedDeliveryForMap.deliveryLong) : undefined}
          geofenceRadius={500}
        />
      )}
    </Box>
  );
};

export default DeliveryMonitoring;
